"use client";

import { forwardRef, useEffect, useMemo, useState } from "react";

import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import Typography from "@/components/flowbite/typography/typography";
import { CheckIcon, NavArrowDownIcon } from "@/components/icons-lib";
import { cn } from "@/shared/helpers/cn.helper";

import type { ListboxFieldProps } from "./types";

const ListboxField = forwardRef<HTMLButtonElement, ListboxFieldProps>(
  (
    {
      label,
      error = false,
      errorMessage,
      required = false,
      hint,
      placeholder = "",
      disabled = false,
      options,
      value,
      onChange,
      defaultValue,
      className,
      name,
      loading = false,
      icon,
      dropdownClassName,
    },
    ref,
  ) => {
    const [selectedValue, setSelectedValue] = useState<
      string | undefined
    >(value ?? defaultValue);

    useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value);
      }
    }, [value]);

    const handleChange = (newValue: string) => {
      setSelectedValue(newValue);
      onChange?.(newValue);
    };

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : selectedValue;

    const selectedOption = useMemo(
      () => options.find((opt) => opt.value === currentValue),
      [options, currentValue],
    );

    const memoizedOptions = useMemo(() => options, [options]);

    const errorId = name ? `${name}-error` : undefined;
    const hintId = name ? `${name}-hint` : undefined;

    const getAriaDescribedBy = () => {
      if (error && errorMessage) return errorId;
      if (hint) return hintId;
      return undefined;
    };

    const getDisplayText = () => {
      if (loading) return "Loading...";
      if (selectedOption) return selectedOption.label;
      return placeholder;
    };

    return (
      <Field className={cn("flex min-w-0 flex-col gap-1", className)}>
        {label && (
          <Label
            className={cn(
              "block",
              Typography.classes.Body.default,
              Typography.classes.Body.classnames.sRegular,
              "text-text-secondary",
            )}
            htmlFor={name}
          >
            {label}
            {required && (
              <span
                aria-label="required"
                className="text-red-default ml-0.5"
              >
                *
              </span>
            )}
          </Label>
        )}

        <Listbox
          aria-describedby={getAriaDescribedBy()}
          aria-invalid={error}
          disabled={disabled || loading}
          name={name}
          value={currentValue ?? ""}
          onChange={handleChange}
        >
          {({ open }) => {
            const isInactiveState = disabled || loading;
            const isDefaultState = !error && !open && !isInactiveState;
            const isFocusedOpenState = !error && open && !isInactiveState;
            const isErrorState = error;

            return (
              <div className="relative min-w-0 w-full">
                <ListboxButton
                  ref={ref}
                  className={cn(
                    "h-10 w-full",
                    "flex items-center justify-between gap-3",
                    "px-3 py-2",
                    "bg-white",
                    "rounded-xl",
                    "border",
                    "focus:outline-none",
                    Typography.classes.Body.default,
                    Typography.classes.Body.classnames.mMedium,
                    "text-light-400",
                    !selectedOption && "text-gray-300",
                    isDefaultState && "border-main-100",
                    isFocusedOpenState && "border-light-600",
                    isErrorState && "border-system-red",
                    isInactiveState && "border-main-100 cursor-not-allowed bg-background-primary opacity-50",
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {icon && <div className="flex-shrink-0">{icon}</div>}
                    <span className="flex-1 truncate text-left">{getDisplayText()}</span>
                  </div>

                  <NavArrowDownIcon
                    className={cn(
                      "flex-shrink-0 transition-transform duration-200",
                      "text-gray-400",
                      open && "rotate-180",
                      isInactiveState && "text-gray-300",
                      !selectedOption && "text-gray-300",
                    )}
                    height={24}
                    width={24}
                  />
                </ListboxButton>

                <ListboxOptions
                  anchor="bottom start"
                  className={cn(
                    "z-50 mt-1 w-[var(--button-width)] overflow-auto p-2",
                    "[--anchor-max-height:15rem]",
                    "rounded-xl bg-white shadow-md",
                    "focus:outline-none",
                    "[&::-webkit-scrollbar-button]:hidden",
                    "[&::-webkit-scrollbar-thumb]:bg-gray-300",
                    "[&::-webkit-scrollbar-thumb]:rounded-full",
                    "[&::-webkit-scrollbar-track]:mt-3",
                    "[&::-webkit-scrollbar-track]:mb-3",
                    "[&::-webkit-scrollbar]:w-1.5",
                    "[&::-webkit-scrollbar]:bg-transparent",
                    "[&::-webkit-scrollbar-track]:bg-transparent",
                    "[&::-webkit-scrollbar-track]:mx-0.5",
                    dropdownClassName,
                  )}
                >
                  {(() => {
                    if (loading) {
                      return (
                        <div className="p-4 text-center text-gray-500">
                          Loading...
                        </div>
                      );
                    }
                    if (memoizedOptions.length === 0) {
                      return (
                        <div className="p-4 text-center text-gray-500">
                          No options available
                        </div>
                      );
                    }
                    return memoizedOptions.map((option) => (
                      <ListboxOption
                        key={option.value}
                        className={({ focus, selected }) =>
                          cn(
                            "flex cursor-pointer select-none items-center justify-between rounded-md px-2 py-2.5",
                            Typography.classes.Body.default,
                            Typography.classes.Body.classnames.mMedium,
                            "text-gray-700",
                            {
                              "bg-gray-100": focus,
                              "text-main-600 bg-background-primary": selected,
                              "pointer-events-none opacity-50": disabled || option.disabled,
                            },
                          )
                        }
                        disabled={disabled || Boolean(option.disabled)}
                        value={option.value}
                      >
                        {({ selected }) => (
                          <>
                            <span className="truncate max-w-[200px]">{option.label}</span>
                            {selected && (
                              <CheckIcon
                                className="flex-shrink-0 text-primary-active"
                                height={24}
                                width={24}
                              />
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ));
                  })()}
                </ListboxOptions>
              </div>
            );
          }}
        </Listbox>
        {error && errorMessage && (
          <p
            className="mt-1 text-xs text-btn-danger"
            id={errorId}
            role="alert"
          >
            {errorMessage}
          </p>
        )}
        {!error && hint && (
          <p
            className="mt-1 text-xs text-text-secondary"
            id={hintId}
          >
            {hint}
          </p>
        )}
      </Field>
    );
  },
);

ListboxField.displayName = "ListboxField";
export default ListboxField;

